import { apiClient, ApiRequestConfig } from '@/services/http';

export type QueryParams = Record<string, unknown>;

export function createResourceService<TEntity, TCreate = Partial<TEntity>, TUpdate = Partial<TEntity>>(
    basePath: `/${string}`,
) {
    return {
        create: (input: TCreate, config?: ApiRequestConfig<TCreate>) =>
            apiClient.post<TEntity, TCreate>(basePath, input, config),
        delete: (id: string, config?: ApiRequestConfig) => apiClient.delete<void>(`${basePath}/${id}`, config),
        duplicate: (id: string) => apiClient.post<TEntity>(`${basePath}/${id}/duplicate`),
        getById: (id: string) => apiClient.get<TEntity>(`${basePath}/${id}`),
        list: <TParams extends QueryParams = QueryParams>(params?: TParams) =>
            apiClient.get<TEntity[], TParams>(basePath, { params }),
        update: (id: string, updates: TUpdate, config?: ApiRequestConfig<TUpdate>) =>
            apiClient.put<TEntity, TUpdate>(`${basePath}/${id}`, updates, config),
        restore: (id: string) => apiClient.put<void>(`${basePath}/${id}/restore`),
        permanentDelete: (id: string, config?: ApiRequestConfig) =>
            apiClient.delete<void>(`${basePath}/${id}/permanent`, config),
    };
}

export function createSanitizedResourceService<TEntity, TCreate, TUpdate>(
    basePath: `/${string}`,
    serializeCreate: (input: Partial<TEntity>) => TCreate,
    serializeUpdate: (input: Partial<TEntity>) => TUpdate,
) {
    const base = createResourceService<TEntity, TCreate, TUpdate>(basePath);

    return {
        ...base,
        create: (input: Partial<TEntity>, config?: ApiRequestConfig<TCreate>) =>
            base.create(serializeCreate(input), config),
        update: (id: string, updates: Partial<TEntity>, config?: ApiRequestConfig<TUpdate>) =>
            base.update(id, serializeUpdate(updates), config),
    };
}
